import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="ui--sidebar"
//
// Hotwire port of the @a-safe-digital/react-sidebar mobile behaviour. The desktop
// panel is static (CSS only); this controller drives ONLY the mobile off-canvas
// drawer: slide the panel in/out (translate class-swap), fade the overlay, and
// lock body scroll while open. Modelled on ui--admin-layout.
//
// External triggers (e.g. a navbar hamburger) open the drawer by dispatching a
// window event — the Hotwire analog of React's setSidebarOpen:
//   window.dispatchEvent(new CustomEvent("sidebar:toggle"))
// (also "sidebar:open" / "sidebar:close"). open/close/toggle are plain actions
// too, for triggers rendered inside the component's scope.
export default class extends Controller {
  static targets = ["panel", "overlay", "desktopPanel", "closeButton"]
  static values = { open: { type: Boolean, default: false } }

  connect() {
    this.handleEscape = this.handleEscape.bind(this)
    this.open = this.open.bind(this)
    this.close = this.close.bind(this)
    this.toggle = this.toggle.bind(this)

    document.addEventListener("keydown", this.handleEscape)
    // Decoupled external triggers (hamburger elsewhere in the DOM).
    window.addEventListener("sidebar:open", this.open)
    window.addEventListener("sidebar:close", this.close)
    window.addEventListener("sidebar:toggle", this.toggle)

    // Rendered already-open (SSR open: true): the panel/overlay classes are set
    // server-side; sync the body scroll-lock so it matches.
    if (this.openValue) {
      document.body.classList.add("overflow-hidden", "lg:overflow-auto")
    }

    // If the desktop rail booted collapsed (the pre-paint FOUC guard set the state
    // class before this controller connected), pull its now-hidden links out of the
    // tab order / a11y tree so the DOM matches what's visible.
    this.syncCollapsedInert()
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleEscape)
    window.removeEventListener("sidebar:open", this.open)
    window.removeEventListener("sidebar:close", this.close)
    window.removeEventListener("sidebar:toggle", this.toggle)
    // Turbo navigation while open would otherwise leave the next page scroll-locked.
    document.body.classList.remove("overflow-hidden", "lg:overflow-auto")
  }

  open() {
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.remove("-translate-x-full")
    this.panelTarget.classList.add("translate-x-0")
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("hidden", "animate-overlay-fadeout")
      this.overlayTarget.classList.add("animate-overlay-fadein")
    }
    // The close button rides the panel (left-full); show it as the panel slides in.
    if (this.hasCloseButtonTarget) {
      this.closeButtonTarget.classList.remove("hidden")
    }
    // Responsive scroll lock: locked on mobile, untouched once the static
    // desktop panel takes over (lg:overflow-auto).
    document.body.classList.add("overflow-hidden", "lg:overflow-auto")
    this.openValue = true
  }

  close() {
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.add("-translate-x-full")
    this.panelTarget.classList.remove("translate-x-0")
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("animate-overlay-fadein")
      this.overlayTarget.classList.add("animate-overlay-fadeout")
      // Hide after the fade-out so the overlay animates out rather than vanishing.
      const overlay = this.overlayTarget
      window.setTimeout(() => {
        if (!this.openValue) overlay.classList.add("hidden")
      }, 500)
    }
    // Hide the close button once the panel has finished sliding out (~duration-300);
    // hiding it immediately would blink it away while it's still riding the panel.
    if (this.hasCloseButtonTarget) {
      const closeButton = this.closeButtonTarget
      window.setTimeout(() => {
        if (!this.openValue) closeButton.classList.add("hidden")
      }, 300)
    }
    document.body.classList.remove("overflow-hidden", "lg:overflow-auto")
    this.openValue = false
  }

  toggle() {
    if (this.openValue) {
      this.close()
    } else {
      this.open()
    }
  }

  handleEscape(event) {
    if (event.key === "Escape" && this.openValue) {
      this.close()
    }
  }

  // ── Desktop collapse (opt-in `collapsible:`) ──────────────────────────────
  // Slide the desktop rail open/closed. State lives in `.admin-sidebar-collapsed`
  // on <html> (applied pre-paint by shared/admin_sidebar_fouc to avoid a flash)
  // and mirrored to localStorage so it persists across Turbo navigations. The
  // slide (rail width) + chevron rotation are CSS (application.css); this just
  // flips the state class.
  toggleCollapse() {
    const collapsed = document.documentElement.classList.toggle(
      "admin-sidebar-collapsed",
    )
    this.persistCollapsed(collapsed ? "1" : "0")
    this.syncCollapsedInert()
  }

  // Keep the collapsed desktop rail out of the tab order + a11y tree. Its links
  // stay in the DOM (the rail animates width→0 with overflow-x:hidden), so without
  // `inert` a keyboard user could Tab into invisible nav items.
  syncCollapsedInert() {
    if (!this.hasDesktopPanelTarget) return
    this.desktopPanelTarget.inert = document.documentElement.classList.contains(
      "admin-sidebar-collapsed",
    )
  }

  persistCollapsed(value) {
    try {
      localStorage.setItem("adminSidebarCollapsed", value)
    } catch (_e) {
      // Private mode / storage disabled — collapse still works for this page.
    }
  }
}
